<template>
  <ul class="idml-tree modal-body--layers">
    <li
      v-for="(node, index) in displayNodes"
      :key="node.properties.id || index"
    >
      <div
        class="d-flex align-items-center"
        :class="{'c-folder-layer__folder': isFolder(node), 'layer--item': !isFolder(node)}"
      >
        <b-form-checkbox
          v-if="selectable"
          class="mr-2"
          :checked="isSelected(node)"
          :disabled="!isImportable(node)"
          :aria-label="`Import ${node.name}`"
          @change="$emit('toggle', {node, selected: $event})"
        />
        <button
          v-if="isFolder(node)"
          type="button"
          class="idml-tree__toggle"
          :aria-expanded="!collapsed[index]"
          :aria-label="`${collapsed[index] ? 'Expand' : 'Collapse'} ${node.name}`"
          @click="$set(collapsed, index, !collapsed[index])"
        >
          <b-icon
            icon="triangle-fill"
            :class="collapsed[index] ? 'closed' : 'opened'"
          />
        </button>
        <button
          type="button"
          class="idml-tree__node d-flex align-items-center"
          :class="{'idml-tree__node--selected': selected === node, 'text-muted': node.visible === false, 'c-folder-layer__item mt-2': !isFolder(node)}"
          :aria-pressed="selected === node"
          :title="node.name || node.properties.kind"
          @click="$emit('select', node)"
        >
          <svg
            v-if="isFolder(node)"
            width="20"
            height="20"
            class="mr-2 flex-shrink-0"
            aria-hidden="true"
          >
            <use href="/img/icons/sprite.svg#file-folder-svg" />
          </svg>
          <span
            v-else
            class="c-folder-layer__item--preview mr-2"
          >
            <b-icon
              :icon="icon(node)"
              class="d-block"
            />
          </span>
          <span class="layer--item__text">{{ node.name || node.properties.kind }}</span>
          <select
            v-if="selectable && !isFolder(node) && isSelected(node) && fieldTypeOptions(node).length > 1"
            class="form-control form-control-sm idml-tree__field-type ml-auto"
            :value="node.__fieldType"
            @click.stop
            @change="$emit('field-type', {node, fieldType: $event.target.value})"
          >
            <option
              v-for="option in fieldTypeOptions(node)"
              :key="option.value"
              :value="option.value"
            >
              {{ option.text }}
            </option>
          </select>
          <span
            v-if="node.properties.image && node.properties.image.requiresUpload"
            class="badge badge-warning ml-2"
          >Image required</span>
          <span
            v-else-if="node.properties.image && !isImportable(node)"
            class="badge badge-warning ml-2"
          >Unsupported image</span>
          <span
            v-if="node.visible === false"
            class="small ml-2"
          >Hidden</span>
        </button>
      </div>
      <b-collapse
        v-if="node.children && node.children.length"
        :visible="!collapsed[index]"
      >
        <IDMLLayersTree
          class="ml-3 mb-2"
          :nodes="node.children"
          :selected="selected"
          :selectable="selectable"
          :selected-ids="selectedIds"
          @select="$emit('select', $event)"
          @toggle="$emit('toggle', $event)"
          @field-type="$emit('field-type', $event)"
        />
      </b-collapse>
    </li>
  </ul>
</template>

<script>
export default {
  name: 'IDMLLayersTree',
  props: {
    nodes: {type: Array, default: () => []},
    selected: {type: Object, default: null},
    selectable: {type: Boolean, default: false},
    selectedIds: {type: Object, default: () => ({})},
  },
  data() {
    return {collapsed: {}};
  },
  computed: {
    displayNodes() {
      return [...this.nodes].reverse();
    },
  },
  methods: {
    isFolder(node) {
      return node.type === 'group' || node.type === 'artboard';
    },
    isImportable(node) {
      if (this.isFolder(node)) return true;
      if (!['text', 'image', 'shape'].includes(node.properties.kind)) return false;
      if (node.properties.kind !== 'image') return true;
      return !node.properties.image.requiresUpload
        && /\.(png|jpe?g|gif|webp|avif|bmp|tiff?)$/i.test(node.properties.image.storedPath || '');
    },
    isSelected(node) {
      if (this.isFolder(node)) {
        const children = [];
        const collect = items => items.forEach(item => {
          if (this.isImportable(item) && !this.isFolder(item)) children.push(item);
          collect(item.children || []);
        });
        collect(node.children || []);
        return children.length > 0 && children.every(child => this.selectedIds[child.__importId]);
      }
      return Boolean(this.selectedIds[node.__importId]);
    },
    fieldTypeOptions(node) {
      return {
        text: [{value: 'Multi Line Text', text: 'Multi Line Text'}],
        image: [{value: 'Background Theme Image', text: 'Background Image'}],
        shape: [{value: 'Rectangle', text: 'Rectangle'}],
      }[node.properties.kind] || [];
    },
    icon(node) {
      return {
        text: 'textarea-t',
        image: 'image',
        shape: 'square',
      }[node.properties.kind] || 'layers';
    },
  },
};
</script>

<style scoped>
.idml-tree {
  list-style: none;
  margin: 0;
  padding: 0;
}

.idml-tree__node {
  flex: 1;
  min-width: 0;
  padding: 0;
  border: 0;
  background: transparent;
  text-align: left;
  color: inherit;
}

.idml-tree__node:hover,
.idml-tree__node--selected {
  background: #e9ecef;
}

.idml-tree__toggle {
  padding: 0;
  margin-right: 8px;
  border: 0;
  background: transparent;
  color: inherit;
}

.c-folder-layer__folder {
  border-top: solid 1px #505050;
  border-bottom: solid 1px #505050;
}

.c-folder-layer__item--preview {
  border: solid 1px #bebebe;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 24px;
}

.layer--item__text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.idml-tree__field-type {
  width: 170px;
  flex-shrink: 0;
}

.closed,
.opened {
  font-size: 11px;
  transition: transform 0.5s;
}

.closed {
  transform: rotate(90deg);
}

.opened {
  transform: rotate(180deg);
}
</style>
